import type { ChecklistAPI, ChecklistContextAPI, ChecklistFieldAPI, ChecklistsNamesAPI, ChecklistsPageAPI, ChecklistVehicleAPI } from './Definitions/APITypes';
import {
  type Checklist,
  type ChecklistContext,
  type ChecklistField,
  type ChecklistFieldContent,
  ChecklistFieldType,
  type ChecklistSelectContent,
  type ChecklistsNames,
  type ChecklistsPage,
  type ChecklistVehicle,
  type TemplateChecklist,
} from './Definitions/Types';

const templateChecklistSelectOptionContent = (optionsContent?: ChecklistSelectContent) => {
  if (optionsContent) {
    return optionsContent.options.map((optionContent) => ({
      label: optionContent.label || '',
      checked: null,
      ...(optionContent.compliance && { compliance: optionContent.compliance }),
    }));
  }

  return {
    label: '',
    checked: null,
  };
};

const templateChecklistFieldContent = (field: { type: ChecklistFieldType; content: ChecklistFieldContent }) => {
  switch (field.type) {
    case ChecklistFieldType.CHECK:
      return { checked: null };
    case ChecklistFieldType.TEXT:
      return { value: null };
    case ChecklistFieldType.MULTI_SELECT:
    case ChecklistFieldType.SINGLE_SELECT:
      return {
        options: templateChecklistSelectOptionContent('options' in field.content ? field.content : undefined),
      };
  }
};

const checklistVehicleTransformer = (checklistVehicleAPI: ChecklistVehicleAPI): ChecklistVehicle => {
  const { device_id: deviceId, license_plate: licensePlate, brand, model, group_id: groupId } = checklistVehicleAPI;

  return {
    deviceId,
    ...(licensePlate && { licensePlate }),
    ...(brand && { brand }),
    ...(model && { model }),
    ...(groupId && { groupId }),
  };
};

const checklistContextTransformer = (checklistContextAPI: ChecklistContextAPI): ChecklistContext => {
  const { app_version: appVersion, address, vehicle, group_path: groupPath } = checklistContextAPI;

  return {
    appVersion,
    ...(address && { address }),
    ...(vehicle && { vehicle: checklistVehicleTransformer(vehicle) }),
    ...(groupPath && { groupPath }),
  };
};

const checklistFieldTransformer = (checklistFieldAPI: ChecklistFieldAPI): ChecklistField => {
  const { id, type, required, title, content, should_request_photos: shouldRequestPhotos, photos_urls: photosUrls, complianceStatus } = checklistFieldAPI;

  return {
    id,
    type,
    required,
    title,
    content,
    shouldRequestPhotos,
    photosUrls,
    complianceStatus,
  };
};

export const checklistTransformer = (checklistAPI: ChecklistAPI): Checklist => {
  const {
    id,
    version,
    name,
    template,
    created_at: createdAt,
    created_by: createdBy,
    fields,
    template_id: templateId,
    template_created_at: templateCreatedAt,
    template_created_by: templateCreatedBy,
    context,
    vehicle_ids: vehicleIds,
    vehicles,
    groups,
    vehicle_group_ids: vehicleGroupIds,
    completed_count: completedCount,
    template_active: templateActive,
    photosPending,
    pendingPhotosFieldCount,
    complianceStatus,
    nonConformFieldCount,
  } = checklistAPI;

  return {
    id,
    version,
    name,
    template,
    createdAt,
    createdBy,
    fields: fields.map(checklistFieldTransformer),
    templateId,
    templateCreatedAt,
    templateCreatedBy,
    context: context ? checklistContextTransformer(context) : null,
    vehicleIds,
    vehicles: vehicles ? vehicles.map((v) => ({ id: v.id, groupId: v.group_id, groupPath: v.group_path })) : [],
    vehicleGroupIds,
    groups: groups ? groups.map((g) => ({ id: g.id, path: g.path })) : [],
    completedCount,
    templateActive,
    photosPending,
    pendingPhotosFieldCount,
    complianceStatus,
    nonConformFieldCount,
  };
};

export const checklistsPageTransformer = (checklistsPage: ChecklistsPageAPI): ChecklistsPage => {
  const checklistsPageContent = checklistsPage.page;

  return {
    page: {
      content: checklistsPageContent.content.map((value) => checklistTransformer(value)),
      last: checklistsPageContent.last,
      totalPages: checklistsPageContent.total_pages,
      totalElements: checklistsPageContent.total_elements,
      numberOfElements: checklistsPageContent.number_of_elements,
      number: checklistsPageContent.number,
      size: checklistsPageContent.size,
      first: checklistsPageContent.first,
      empty: checklistsPageContent.empty,
    },
  };
};

export const checklistsNamesTransformer = (checklistsNames: ChecklistsNamesAPI): ChecklistsNames => ({
  names: checklistsNames.names,
});

export const templateChecklistTransformer = ({ vehicleIds, vehicleGroupIds, ...templateChecklist }: TemplateChecklist) => ({
  ...templateChecklist,
  vehicle_ids: vehicleIds || null,
  vehicle_group_ids: vehicleGroupIds || null,
  fields: templateChecklist.fields.map(({ shouldRequestPhotos, ...field }) => ({
    ...field,
    should_request_photos: shouldRequestPhotos,
    content: JSON.stringify(templateChecklistFieldContent(field)),
  })),
});
